import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Search, User, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" }
];

const Header = () => {
  const { count, setIsOpen } = useCart();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <>
      <div className="w-full bg-[#c9a24b] text-[#0b0a09] text-center text-[12px] tracking-[0.18em] uppercase py-2 font-medium">
        Rooted in Tradition · Powered by Nature · Free Shipping Over $60
      </div>
      <header
        className={`sticky top-0 z-50 transition-all duration-500 border-b ${
          scrolled
            ? "bg-[#0b0a09]/95 backdrop-blur-md border-[#2c2822] py-3"
            : "bg-transparent border-transparent py-5"
        }`}
      >
        <div className="max-w-[1280px] mx-auto px-6 flex items-center justify-between">
          <button
            className="lg:hidden text-[#f4efe4]"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>

          <Link to="/" className="flex flex-col items-center lg:items-start leading-none">
            <span className="font-display text-[26px] md:text-[30px] text-gold-gradient font-semibold tracking-wide">
              Kanama
            </span>
            <span className="font-display text-[15px] md:text-[17px] text-[#f4efe4] tracking-[0.35em] uppercase -mt-1">
              Naturals
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `kn-link text-[13px] tracking-[0.2em] uppercase transition-colors ${
                    isActive ? "text-[#c9a24b]" : "text-[#f4efe4] hover:text-[#c9a24b]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-5 text-[#f4efe4]">
            <button className="hidden sm:block hover:text-[#c9a24b] transition-colors" aria-label="Search">
              <Search size={20} />
            </button>
            <Link
              to={user ? "/account" : "/login"}
              className="hidden sm:block hover:text-[#c9a24b] transition-colors"
              aria-label="Account"
              data-testid="header-account-link"
            >
              <User size={20} />
            </Link>
            <button
              onClick={() => setIsOpen(true)}
              className="relative hover:text-[#c9a24b] transition-colors"
              aria-label="Open cart"
            >
              <ShoppingBag size={22} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#c9a24b] text-[#0b0a09] text-[10px] font-semibold w-[18px] h-[18px] rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden transition-opacity duration-300 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
        <div
          className={`absolute left-0 top-0 h-full w-[80%] max-w-[320px] bg-[#141210] border-r border-[#2c2822] p-8 transition-transform duration-400 ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex justify-between items-center mb-10">
            <span className="font-display text-2xl text-gold-gradient font-semibold">Kanama</span>
            <button onClick={() => setMobileOpen(false)} className="text-[#f4efe4]">
              <X size={24} />
            </button>
          </div>
          <nav className="flex flex-col gap-6">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `text-lg tracking-[0.15em] uppercase ${
                    isActive ? "text-[#c9a24b]" : "text-[#f4efe4]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
};

export default Header;
